import { api, type TableExportPayload } from "./api.ts";
import { minorUnits } from "./format.ts";

/**
 * Выгрузка в Excel списка, открытого под цифрой.
 *
 * Колонки описывает сам экран — рядом с таблицей, теми же подписями. Так файл
 * повторяет то, что человек видит: те же столбцы в том же порядке, а то, что в
 * ячейке стоит второй строкой (телефон, бронь, «в CRM: …»), уходит отдельной
 * колонкой рядом, чтобы по ней можно было фильтровать.
 *
 * Выгружается ВЕСЬ список под цифрой, а не страница на экране: число на плитке
 * — это все строки, и файл на двести строк под числом 13 104 был бы не тем
 * списком.
 */

export type ExportKind = NonNullable<TableExportPayload["columns"][number]["kind"]>;
export type ExportCell = string | number | null;

export type ExportColumn<R> = {
  title: string;
  kind?: ExportKind;
  width?: number;
  value: (r: R) => ExportCell | undefined;
};

/** Предел строк — тот же, что у сервера (`TABLE_EXPORT_MAX_ROWS`). */
export const EXPORT_MAX_ROWS = 25_000;

/** Деньги из минорных единиц — числом в валюте, как в остальных выгрузках. */
export function moneyValue(minor: string | number | null | undefined, currency = "AED"): number | null {
  if (minor === null || minor === undefined || minor === "") return null;
  const n = Number(minor);
  return Number.isFinite(n) ? n / minorUnits(currency) : null;
}

/**
 * Все страницы списка подряд. `onProgress` получает, сколько уже собрано и
 * сколько всего, — кнопка показывает это, пока идёт выгрузка.
 */
export async function fetchAllPages<R>(
  page: (limit: number, offset: number) => Promise<{ total: number; rows: R[] }>, // i18n-ok: тип TypeScript, а не текст на экране
  onProgress?: (done: number, total: number) => void,
): Promise<R[]> {
  const out: R[] = [];
  let total = Infinity;
  while (out.length < total) {
    const p = await page(500, out.length);
    total = p.total;
    if (total > EXPORT_MAX_ROWS) {
      throw new Error(`${total} > ${EXPORT_MAX_ROWS}`);
    }
    out.push(...p.rows);
    onProgress?.(out.length, total);
    if (p.rows.length === 0) break;
  }
  return out;
}

/** Имя файла из заголовка панели: символы, которые не любит файловая система, — прочь. */
function fileName(title: string): string {
  /* Дата по часам человека, а не по UTC: ночью в Дубае UTC ещё вчера. */
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const clean = title.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "export";
  return `${clean} ${today}.xlsx`;
}

function cells<R>(columns: Array<ExportColumn<R>>, rows: readonly R[]): TableExportPayload["rows"] {
  return rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      if (v === undefined || v === null || v === "") return null;
      return typeof v === "number" && !Number.isFinite(v) ? null : v;
    }),
  );
}

export async function exportToExcel<R>(title: string, columns: Array<ExportColumn<R>>, rows: readonly R[]): Promise<void> {
  await download(title, {
    title,
    columns: columns.map((c) => ({ title: c.title, kind: c.kind, width: c.width })),
    rows: cells(columns, rows),
  });
}

/**
 * Тот же список, разложенный по листам одного файла с одинаковыми колонками.
 * Лист без строк остаётся в книге с одной шапкой: пустой лист «Сделки» —
 * ответ «таких нет», а пропавший лист заставил бы гадать.
 */
export async function exportSheetsToExcel<R>(
  title: string,
  columns: Array<ExportColumn<R>>,
  sheets: Array<{ name: string; rows: readonly R[] }>,
): Promise<void> {
  await download(title, {
    title,
    columns: columns.map((c) => ({ title: c.title, kind: c.kind, width: c.width })),
    rows: [],
    sheets: sheets.map((s) => ({ name: s.name, rows: cells(columns, s.rows) })),
  });
}

async function download(title: string, payload: TableExportPayload): Promise<void> {
  const blob = await api.exportTable(payload);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName(title);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
